import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Modal,
  Layout,
  Text,
  Padding,
  AnchorLink,
} from "@kidslivesafe/sc-react";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  createCustomerRedisTokenAsync,
  sendMemberEventAsync,
} from "../../features/admin/adminSlice";
import { selectBrandInfo } from "../../features/brand/brandSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { formatPhoneNumber } from "../../utils";
import { unPauseSubscriptionAsync } from "../../features/subscriptions/subscriptionSlice";
import { useUnpauseSubscription } from "../../hooks/pauseHook";

export interface UnpauseModalProps {
  toggleModal?: string;
}
const UnpauseModal: React.FC<UnpauseModalProps> = ({ toggleModal }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pageURL = location.pathname;
  const brandInfo = useAppSelector(selectBrandInfo);
  const handleUnpause = useUnpauseSubscription();
  const [retryClicked, setRetryClicked] = useState<boolean>(false);
  const [retrySuccess, setRetrySuccess] = useState<boolean>(false);
  const [showUnpauseModal, setShowUnpauseModal] = useState<boolean>(false);
  const [retryLoading, setRetryLoading] = useState<boolean>(false);

  const hasSentModalDisplayEvent = useRef(false);

  useEffect(() => {
    if (toggleModal) {
      setShowUnpauseModal(true);
      if (!hasSentModalDisplayEvent.current) {
        dispatch(
          sendMemberEventAsync({
            EventCategory: "Modal Display",
            EventAction: "Retry",
            MetaData: JSON.stringify({
              PageName: pageURL,
              Flow: "unpause",
            }),
          }),
        );
        hasSentModalDisplayEvent.current = true;
      }
    }
  }, [toggleModal]);

  const handleResumePayment = async () => {
    setRetryLoading(true);
    try {
      const result = await handleUnpause();
      if (
        result &&
        result.type &&
        result.type === unPauseSubscriptionAsync.fulfilled.type
      ) {
        setRetryClicked(true);
        setRetrySuccess(true);
        setRetryLoading(false);
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        setRetryClicked(true);
        setRetrySuccess(false);
        setRetryLoading(false);
      }
    } catch (error) {
      console.error("Resume failed:", error);
      setRetryClicked(true);
      setRetrySuccess(false);
      setRetryLoading(false);
    }
  };
  const handleExternalNavigation = async () => {
    const createCustomerRedisTokenAsyncResponse = await dispatch(
      createCustomerRedisTokenAsync(),
    );

    dispatch(
      sendMemberEventAsync({
        EventCategory: "Button Click",
        EventAction: "Update CC",
        MetaData: JSON.stringify({
          PageName: pageURL,
          Flow: "unpause",
        }),
      }),
    );

    if (
      createCustomerRedisTokenAsync.fulfilled.match(
        createCustomerRedisTokenAsyncResponse,
      )
    ) {
      const uuid = createCustomerRedisTokenAsyncResponse.payload.token;
      window.location.href = brandInfo.homeUrl + "/fix-payment?uuid=" + uuid;
    }
  };
  return (
    <>
      <Modal
        open={showUnpauseModal}
        setOpen={() => {
          setShowUnpauseModal(false);
        }}
        size="fit-content"
        xClose={true}
        outSideClose={true}
        scrollable={false}
      >
        <Padding
          space="sm"
          top="lg"
          breakpointStyles={{ mobile: { space: "none" } }}
        >
          <Layout
            type="flex"
            flexProps={{
              direction: "column",
              gap: "md",
              alignItems: "center",
            }}
            breakpointStyles={{
              desktop: {
                type: "flex",
                flexProps: {
                  direction: "column",
                  gap: "xl",
                  alignItems: "center",
                },
              },
            }}
          >
            {!retryClicked && !retrySuccess && toggleModal ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "sm",
                  }}
                  breakpointStyles={{
                    desktop: {
                      type: "flex",
                      flexProps: {
                        direction: "column",
                        gap: "xl",
                        alignItems: "center",
                      },
                    },
                  }}
                >
                  <div>
                    <Text textAlign="center" tag="h2">
                      Confirm Your Payment
                    </Text>
                  </div>
                  <Text textAlign="center" size="sm">
                    Would you like to be billed using your current payment
                    method or use a new&nbsp;one?
                  </Text>
                </Layout>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "md",
                    alignItems: "center",
                  }}
                  fillHorizontal
                >
                  <Button
                    buttonText="Current Method"
                    fill={"default"}
                    handleClick={handleResumePayment}
                    clicked={retryClicked}
                    loading={retryLoading}
                  />
                  <AnchorLink
                    handleClick={handleExternalNavigation}
                    stayUnderline
                    role="button"
                  >
                    Update Payment Method
                  </AnchorLink>
                </Layout>

                <Text textAlign="center" size="sm">
                  For questions please contact member support at{" "}
                  <AnchorLink
                    inline
                    phoneNumber
                    hoverUnderline
                    href={brandInfo.phoneNumber}
                  >
                    {formatPhoneNumber(brandInfo.phoneNumber)}
                  </AnchorLink>
                  .
                </Text>
              </>
            ) : retryClicked && !retrySuccess ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "sm",
                  }}
                >
                  <div>
                    <Text textAlign="center" tag="h2" fontWeight="semibold">
                      We've encountered a problem!{" "}
                    </Text>
                  </div>
                  <Text textAlign="center" size="sm">
                    We couldn’t process your payment, possibly due to an expired
                    or declined card.
                  </Text>
                  <Text textAlign="center" size="sm">
                    Please update your payment details to maintain access to
                    your full subscription.
                  </Text>
                </Layout>
                <Button
                  buttonText="Update Payment Method"
                  handleClick={handleExternalNavigation}
                  size="md"
                  fill="fill"
                />
                <Text textAlign="center" size="sm">
                  For questions please contact member support at{" "}
                  <AnchorLink
                    inline
                    phoneNumber
                    hoverUnderline
                    href={brandInfo.phoneNumber}
                  >
                    {formatPhoneNumber(brandInfo.phoneNumber)}
                  </AnchorLink>
                  .
                </Text>
              </>
            ) : retryClicked && retrySuccess && showUnpauseModal ? (
              <>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "column",
                    gap: "lg",
                  }}
                >
                  <div>
                    <Text textAlign="center" tag="h2" fontWeight="semibold">
                      Success!{" "}
                    </Text>
                    <Text textAlign="center" size="sm">
                      We were able to successfully process your payment.
                    </Text>
                  </div>
                </Layout>
                <Button
                  buttonText="Go to Dashboard"
                  handleClick={() => {
                    setShowUnpauseModal(false);
                  }}
                  color="success"
                  cursor="pointer"
                />
              </>
            ) : null}
          </Layout>
        </Padding>
      </Modal>
    </>
  );
};
export default UnpauseModal;
